// src/components/Downloads.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Modal,
  Box,
  TextField,
  IconButton,
  Container,
  Chip,
  Card,
  CardContent,
  Fade,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  InputAdornment,
  Tooltip,
  Avatar,
  Grid,
  CardActions,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  Logout,
  Dashboard,
  CloudDownload,
  Close,
  CheckCircle,
  Error as ErrorIcon,
  ArrowBack,
  Search,
  Info,
  GetApp,
  Description,
  Numbers,
  CalendarToday,
  CloudUpload,
  Person,
} from '@mui/icons-material';
import { ref, set, get, child, remove } from 'firebase/database';
import { auth, database, storage } from '../firebase/Config';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 4,
};

const Downloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [filteredDownloads, setFilteredDownloads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [newDownload, setNewDownload] = useState({
    name: '',
    version: '',
    details: '',
    file: null,
  });
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userEmail, setUserEmail] = useState(null);
  const navigate = useNavigate();

  // Estados para validação e feedback
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    downloadId: null,
    downloadName: '',
  });

  const { userRole } = useAuth();

  // Monitorar a autenticação
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserEmail(user.email);
      } else {
        setUserEmail(null);
      }
    });

    return () => unsubscribe();
  }, []);

  // Funções de feedback
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Validação de formulário
  const validateForm = () => {
    const newErrors = {};

    if (!newDownload.name || !newDownload.name.trim()) {
      newErrors.name = 'Nome do instalador é obrigatório';
    } else if (newDownload.name.length < 3) {
      newErrors.name = 'Nome deve ter pelo menos 3 caracteres';
    }

    if (!newDownload.version || !newDownload.version.trim()) {
      newErrors.version = 'Número da versão é obrigatório';
    }

    if (!newDownload.details || !newDownload.details.trim()) {
      newErrors.details = 'Detalhes da versão são obrigatórios';
    }

    if (!newDownload.file) {
      newErrors.file = 'Arquivo .exe é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpen = () => {
    setNewDownload({
      name: '',
      version: '',
      details: '',
      file: null,
    });
    setErrors({});
    setUploadProgress(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrors({});
    setUploadProgress(0);
  };

  const handleDetailsOpen = (download) => {
    setSelectedDownload(download);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setSelectedDownload(null);
  };

  const fetchDownloads = async () => {
    setLoading(true);
    const dbRef = ref(database);
    try {
      const snapshot = await get(child(dbRef, 'downloads'));
      if (snapshot.exists()) {
        const downloadsData = snapshot.val();
        const downloadsArray = Object.keys(downloadsData).map((key) => ({
          id: key,
          ...downloadsData[key],
        }));
        // Ordenar por data mais recente
        downloadsArray.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        setDownloads(downloadsArray);
        setFilteredDownloads(downloadsArray);
      } else {
        setDownloads([]);
        setFilteredDownloads([]);
      }
    } catch (error) {
      console.error('Erro ao buscar downloads: ', error);
      showSnackbar('Erro ao carregar downloads', 'error');
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validar tipo de arquivo
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.exe') && !fileName.endsWith('.msi')) {
        showSnackbar('Por favor, selecione apenas arquivos .exe ou .msi', 'error');
        e.target.value = null;
        return;
      }

      // Validar tamanho (máx 500MB)
      if (file.size > 500 * 1024 * 1024) {
        showSnackbar('O arquivo deve ter no máximo 500MB', 'error');
        e.target.value = null;
        return;
      }

      setNewDownload({ ...newDownload, file });
      if (errors.file) setErrors({ ...errors, file: null });
    }
  };

  const handleAddDownload = async () => {
    if (!validateForm()) {
      showSnackbar('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const downloadId = `download_${new Date().getTime()}`;
      const fileName = newDownload.file.name;

      // Upload do arquivo para o Storage
      setUploadProgress(10);
      const fileRef = storageRef(storage, `downloads/${downloadId}/${fileName}`);
      await uploadBytes(fileRef, newDownload.file);
      setUploadProgress(70);

      const fileURL = await getDownloadURL(fileRef);
      setUploadProgress(90);

      // Salvar no database
      await set(ref(database, `downloads/${downloadId}`), {
        id: downloadId,
        name: newDownload.name.trim(),
        version: newDownload.version.trim(),
        details: newDownload.details.trim(),
        fileURL: fileURL,
        fileName: fileName,
        fileSize: newDownload.file.size,
        uploadDate: new Date().toISOString(),
        uploadedBy: userEmail,
      });

      setUploadProgress(100);
      showSnackbar('Instalador cadastrado com sucesso!', 'success');
      fetchDownloads();
      handleClose();
    } catch (error) {
      console.error('Erro ao adicionar download: ', error);
      showSnackbar('Erro ao cadastrar instalador', 'error');
    } finally {
      setSubmitLoading(false);
      setUploadProgress(0);
    }
  };

  const handleOpenDeleteDialog = (download) => {
    setDeleteDialog({
      open: true,
      downloadId: download.id,
      downloadName: download.name,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, downloadId: null, downloadName: '' });
  };

  const handleDeleteDownload = async () => {
    try {
      await remove(ref(database, `downloads/${deleteDialog.downloadId}`));
      showSnackbar('Instalador excluído com sucesso!', 'success');
      fetchDownloads();
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Erro ao deletar download: ', error);
      showSnackbar('Erro ao excluir instalador', 'error');
    }
  };

  const handleDownloadFile = async (download) => {
    try {
      const link = document.createElement('a');
      link.href = download.fileURL;
      link.download = download.fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSnackbar('Download iniciado!', 'success');
    } catch (error) {
      console.error('Erro ao fazer download: ', error);
      showSnackbar('Erro ao iniciar download', 'error');
    }
  };

  // Função de pesquisa
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);

    if (value === '') {
      setFilteredDownloads(downloads);
    } else {
      const filtered = downloads.filter((download) => {
        const name = (download.name || '').toLowerCase();
        const version = (download.version || '').toLowerCase();
        const details = (download.details || '').toLowerCase();

        return (
          name.includes(value) ||
          version.includes(value) ||
          details.includes(value)
        );
      });
      setFilteredDownloads(filtered);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilteredDownloads(downloads);
  };

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  useEffect(() => {
    fetchDownloads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(90deg, #48bb78 0%, #38a169 100%)',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            onClick={() => navigate('/home')}
            sx={{
              color: '#fff',
              mr: 2,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <ArrowBack />
          </IconButton>
          <Dashboard sx={{ mr: 2, fontSize: 32 }} />
          <Typography
            variant="h5"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              letterSpacing: '0.5px',
            }}
          >
            Gerenciamento de Downloads
          </Typography>
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                avatar={<Avatar sx={{ bgcolor: '#fff', color: '#48bb78' }}>{userEmail.charAt(0).toUpperCase()}</Avatar>}
                label={userEmail}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: '#fff',
                  fontWeight: 500,
                  px: 1,
                }}
              />
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Logout />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ mt: 4, pb: 4 }}>
        <Fade in timeout={600}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#2d3748',
                      mb: 1,
                    }}
                  >
                    <CloudDownload sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32, color: '#48bb78' }} />
                    Instaladores Disponíveis
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#718096' }}>
                    Total de instaladores: <strong>{downloads.length}</strong>
                    {searchTerm && ` | Filtrados: ${filteredDownloads.length}`}
                  </Typography>
                </Box>
                {userRole === 'admin' && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{
                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                      color: '#fff',
                      fontWeight: 600,
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: '0 4px 15px rgba(72, 187, 120, 0.4)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(72, 187, 120, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s',
                  }}
                >
                  Adicionar Instalador
                </Button>
                )}
              </Box>

              {/* Barra de Pesquisa */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Pesquisar por nome, versão ou detalhes..."
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#718096' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClearSearch}
                          size="small"
                          sx={{ color: '#718096' }}
                        >
                          <Close />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#f7fafc',
                      '&:hover': {
                        bgcolor: '#edf2f7',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#fff',
                      },
                    },
                  }}
                />
              </Box>

              {/* Cards de Downloads */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress size={60} sx={{ color: '#48bb78' }} />
                </Box>
              ) : filteredDownloads.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <CloudDownload sx={{ fontSize: 80, color: '#cbd5e0', mb: 2 }} />
                  <Typography variant="h6" sx={{ color: '#718096', mb: 1 }}>
                    {searchTerm ? 'Nenhum instalador encontrado' : 'Nenhum instalador disponível'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                    {searchTerm ? 'Tente ajustar os termos de busca' : 'Aguardando novos instaladores'}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {filteredDownloads.map((download) => (
                    <Grid item xs={12} sm={6} md={4} key={download.id}>
                      <Card
                        elevation={0}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 3,
                          border: '1px solid #e2e8f0',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 24px rgba(72, 187, 120, 0.15)',
                            borderColor: '#48bb78',
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                          {/* Header do Card */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              sx={{
                                width: 56,
                                height: 56,
                                background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                                mr: 2,
                              }}
                            >
                              <Description sx={{ fontSize: 32 }} />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: '#2d3748',
                                  mb: 0.5,
                                  lineHeight: 1.2,
                                }}
                              >
                                {download.name}
                              </Typography>
                              <Chip
                                label={`v${download.version}`}
                                size="small"
                                sx={{
                                  bgcolor: '#e6ffed',
                                  color: '#48bb78',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          {/* Detalhes */}
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4a5568',
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                minHeight: 60,
                              }}
                            >
                              {download.details || 'Sem descrição disponível'}
                            </Typography>
                          </Box>

                          {/* Data de Upload */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CalendarToday sx={{ fontSize: 16, color: '#718096' }} />
                            <Typography variant="caption" sx={{ color: '#718096' }}>
                              {formatDate(download.uploadDate)}
                            </Typography>
                          </Box>
                        </CardContent>

                        <Divider />

                        {/* Ações */}
                        <CardActions sx={{ p: 2, gap: 1, justifyContent: 'space-between' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Info />}
                            onClick={() => handleDetailsOpen(download)}
                            sx={{
                              borderColor: '#48bb78',
                              color: '#48bb78',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#38a169',
                                bgcolor: '#e6ffed',
                              },
                            }}
                          >
                            Detalhes
                          </Button>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Baixar">
                              <IconButton
                                onClick={() => handleDownloadFile(download)}
                                sx={{
                                  color: '#48bb78',
                                  bgcolor: '#f0fff4',
                                  '&:hover': {
                                    bgcolor: '#c6f6d5',
                                  },
                                }}
                              >
                                <GetApp />
                              </IconButton>
                            </Tooltip>
                            {userRole === 'admin' && (
                              <Tooltip title="Excluir">
                                <IconButton
                                  onClick={() => handleOpenDeleteDialog(download)}
                                  sx={{
                                    color: '#e53935',
                                    bgcolor: '#fff5f5',
                                    '&:hover': {
                                      bgcolor: '#fed7d7',
                                    },
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Modal de Cadastro */}
        <Modal open={open} onClose={handleClose}>
          <Fade in={open}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  Adicionar Novo Instalador
                </Typography>
                <IconButton onClick={handleClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              <TextField
                label="Nome do Instalador"
                fullWidth
                margin="normal"
                required
                value={newDownload.name}
                onChange={(e) => {
                  setNewDownload({ ...newDownload, name: e.target.value });
                  if (errors.name) setErrors({ ...errors, name: null });
                }}
                error={!!errors.name}
                helperText={errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Description sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Número da Versão"
                fullWidth
                margin="normal"
                required
                placeholder="Ex: 1.0.0"
                value={newDownload.version}
                onChange={(e) => {
                  setNewDownload({ ...newDownload, version: e.target.value });
                  if (errors.version) setErrors({ ...errors, version: null });
                }}
                error={!!errors.version}
                helperText={errors.version}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Numbers sx={{ color: '#718096' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Detalhes da Versão"
                fullWidth
                margin="normal"
                required
                multiline
                rows={3}
                placeholder="Descreva as novidades, correções e melhorias desta versão..."
                value={newDownload.details}
                onChange={(e) => {
                  setNewDownload({ ...newDownload, details: e.target.value });
                  if (errors.details) setErrors({ ...errors, details: null });
                }}
                error={!!errors.details}
                helperText={errors.details}
              />

              <Box sx={{ mt: 2, mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    py: 2,
                    borderColor: newDownload.file ? '#48bb78' : '#cbd5e0',
                    color: newDownload.file ? '#48bb78' : '#718096',
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#48bb78',
                      bgcolor: '#48bb7810',
                    },
                  }}
                >
                  {newDownload.file ? newDownload.file.name : 'Selecionar arquivo .exe ou .msi *'}
                  <input
                    type="file"
                    hidden
                    accept=".exe,.msi"
                    onChange={handleFileChange}
                  />
                </Button>
                {errors.file && (
                  <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, ml: 1.5, display: 'block' }}>
                    {errors.file}
                  </Typography>
                )}
                {newDownload.file && (
                  <Typography variant="caption" sx={{ color: '#718096', mt: 1, display: 'block', textAlign: 'center' }}>
                    Tamanho: {formatFileSize(newDownload.file.size)}
                  </Typography>
                )}
              </Box>

              {uploadProgress > 0 && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: '#e0e0e0',
                          borderRadius: 4,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${uploadProgress}%`,
                            height: '100%',
                            bgcolor: '#48bb78',
                            transition: 'width 0.3s',
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#718096', minWidth: 45 }}>
                      {uploadProgress}%
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleClose}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    borderColor: '#718096',
                    color: '#718096',
                    '&:hover': {
                      borderColor: '#2d3748',
                      bgcolor: '#f7fafc',
                    },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleAddDownload}
                  disabled={submitLoading}
                  sx={{
                    py: 1.5,
                    background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                    color: '#fff',
                    fontWeight: 600,
                    '&:hover': {
                      boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                    },
                    '&:disabled': {
                      background: '#cbd5e0',
                    },
                  }}
                >
                  {submitLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    <>
                      <CheckCircle sx={{ mr: 1 }} />
                      Adicionar Instalador
                    </>
                  )}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>

        {/* Modal de Detalhes */}
        <Modal open={detailsOpen} onClose={handleDetailsClose}>
          <Fade in={detailsOpen}>
            <Box sx={modalStyle}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#2d3748',
                  }}
                >
                  Detalhes do Instalador
                </Typography>
                <IconButton onClick={handleDetailsClose} sx={{ color: '#718096' }}>
                  <Close />
                </IconButton>
              </Box>

              {selectedDownload && (
                <>
                  <Box sx={{ mb: 3, p: 3, bgcolor: '#f7fafc', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CloudDownload sx={{ fontSize: 48, color: '#48bb78' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748' }}>
                          {selectedDownload.name}
                        </Typography>
                        <Chip
                          label={`Versão ${selectedDownload.version}`}
                          size="small"
                          sx={{
                            bgcolor: '#48bb78',
                            color: '#fff',
                            fontWeight: 600,
                            mt: 0.5,
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2d3748', mb: 1 }}>
                      Detalhes da Versão:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096', whiteSpace: 'pre-wrap' }}>
                      {selectedDownload.details}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description sx={{ fontSize: 20, color: '#718096' }} />
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        <strong>Arquivo:</strong> {selectedDownload.fileName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Numbers sx={{ fontSize: 20, color: '#718096' }} />
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        <strong>Tamanho:</strong> {formatFileSize(selectedDownload.fileSize)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday sx={{ fontSize: 20, color: '#718096' }} />
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        <strong>Data de Upload:</strong> {formatDate(selectedDownload.uploadDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ fontSize: 20, color: '#718096' }} />
                      <Typography variant="body2" sx={{ color: '#718096' }}>
                        <strong>Enviado por:</strong> {selectedDownload.uploadedBy}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={<GetApp />}
                    onClick={() => {
                      handleDownloadFile(selectedDownload);
                      handleDetailsClose();
                    }}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                      color: '#fff',
                      fontWeight: 600,
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(72, 187, 120, 0.4)',
                      },
                    }}
                  >
                    Fazer Download
                  </Button>
                </>
              )}
            </Box>
          </Fade>
        </Modal>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog
          open={deleteDialog.open}
          onClose={handleCloseDeleteDialog}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorIcon sx={{ color: '#e53935' }} />
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o instalador <strong>{deleteDialog.downloadName}</strong>?
              <br /><br />
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseDeleteDialog}
              sx={{ color: '#718096' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteDownload}
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                '&:hover': { bgcolor: '#c62828' }
              }}
              startIcon={<Delete />}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para Feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Downloads;
